import {Assert, GetValues_ForSchema} from "js-vextensions";
import {AddSchema} from "mobx-graphlink";
import {TriggerSet} from "../../@TriggerSet.js";
import {EngineConfig} from "../../@EngineConfig.js";
import {EngineConfig_XAlarm, EngineConfig_XAlarm_schema} from "../@EC_Alarms.js";
import {EffectPointer} from "../../../@Shared/EffectPointer.js";

export class AlarmSequence {
	constructor(data: Partial<AlarmSequence>) { Object.assign(this, data); }

	name = "NewSequence";
	enabled = true;
	alarms = [] as EngineConfig_XAlarm[];
}
AddSchema("AlarmSequence", {
	properties: {
		name: {type: "string"},
		enabled: {type: "boolean"},
		alarms: {items: EngineConfig_XAlarm_schema},
	},
});

export function SequenceIsSemiEnabled(sequence: AlarmSequence) {
	// SleepEnder sequence may be "disabled" (ie. not part of mix), but it acts like its enabled for some things. (eg. comp creation)
	return sequence.name == "SleepEnder";
}

export const AlarmsGroup_values = ["eeg", "alarms"] as const; // these must match the root comp-config keys in engine-config structure
//export type AlarmsGroup = "eeg"|"snoozeAndAlarms";
export type AlarmsGroup = typeof AlarmsGroup_values[number];
AddSchema("AlarmsGroup", {oneOf: AlarmsGroup_values.map(a=>({const: a}))});
export function GetAlarmSequenceFromAlarmGroup(group: AlarmsGroup, config: EngineConfig, sequenceIndex: number) {
	if (group == "eeg") return config.eeg.alarmSequence;
	if (group == "alarms") return config.alarms.phaseAlarm_sequences[sequenceIndex];
	Assert(false, "Invalid alarms group.");
}

export enum AlarmType {
	Sound = 10,
	Countdown = 15,
	Shake = 20,
	Light = 30,
	//Memory = 40,
	Script = 50,
}
AddSchema("AlarmType", {oneOf: GetValues_ForSchema(AlarmType)});
export enum AlarmTriggerType {
	Time = 10,
	EEG = 20,
}
AddSchema("AlarmTriggerType", {oneOf: GetValues_ForSchema(AlarmTriggerType)});

export function GetEffectGroupForAlarmType(type: AlarmType) {
	switch (type) {
		case AlarmType.Sound: return "sounds";
		case AlarmType.Countdown: return "sounds";
		case AlarmType.Shake: return "shakes";
		case AlarmType.Light: return "lights";
		case AlarmType.Script: return "scripts";
		default: { Assert(false, "Invalid alarm-type."); }
	}
}

export class AlarmConfigBase {
	constructor(data: PartialBy<AlarmConfigBase,
		"enabled" | "effectPointer" |
		"startOffset" | "startOffset_relative" |
		"autoEndAfter" | "autoRestartAfter" | "effectInterval" |
		"intensityStart" | "fadeIn_enabled" | "fadeIn_intensityEnd" | "fadeIn_duration" | "fadeIn_anchoredToSequence" | "fadeIn_curve" |
		"chance_startValue" | "chance_fadeIn_enabled" | "chance_fadeIn_endValue" | "chance_fadeIn_duration" | "chance_fadeIn_anchoredToSequence" | "chance_fadeIn_curve"
	>) {
		Object.assign(this, data);
	}

	enabled = true;
	type: AlarmType;
	//triggerType: AlarmTriggerType;
	//effectTag?: string;
	effectPointer = new EffectPointer();

	startOffset = 0; // in seconds (if time trigger); in activity points (if eye-move trigger)
	/** If true, start-offset is relative to the end of the previous alarm in sequence; else, relative to start of sequence. */
	startOffset_relative: boolean|n;

	autoEndAfter = -1; // in seconds (-1 means never auto-end)
	autoRestartAfter = -1; // in seconds (-1 means never auto-restart)
	effectInterval = 1; // in seconds (if time trigger: <=0 means only play once); in activity points (if eye-move trigger)

	intensityStart = 1; // 0-1
	fadeIn_enabled = false;
	fadeIn_intensityEnd = 1; // 0-1
	fadeIn_duration = 10; // in seconds
	fadeIn_anchoredToSequence: boolean|n;
	fadeIn_curve = 0; // 1 = linear, <1 = faster increases toward start, >1 = faster increases toward end

	chance_startValue = 1; // 0-1
	chance_fadeIn_enabled = false;
	chance_fadeIn_endValue = 1; // 0-1
	chance_fadeIn_duration = 10; // in seconds
	chance_fadeIn_anchoredToSequence: boolean|n;
	chance_fadeIn_curve = 0; // 1 = linear, <1 = faster increases toward start, >1 = faster increases toward end
}
//AddSchema("AlarmConfig", {
export const AlarmConfigBase_SchemaProps = {
	enabled: {type: "boolean"},
	type: {$ref: "AlarmType"},
	effectPointer: {$ref: "EffectPointer"},

	startOffset: {type: "number"},
	startOffset_relative: {type: ["boolean", "null"]},

	autoEndAfter: {type: "number"},
	autoRestartAfter: {type: "number"},
	effectInterval: {type: "number"},

	intensityStart: {type: "number"},
	fadeIn_enabled: {type: "boolean"},
	fadeIn_intensityEnd: {type: "number"},
	fadeIn_duration: {type: "number"},
	fadeIn_anchoredToSequence: {type: ["boolean", "null"]},
	fadeIn_curve: {type: "number"},

	chance_startValue: {type: "number"},
	chance_fadeIn_enabled: {type: "boolean"},
	chance_fadeIn_endValue: {type: "number"},
	chance_fadeIn_duration: {type: "number"},
	chance_fadeIn_anchoredToSequence: {type: ["boolean", "null"]},
	chance_fadeIn_curve: {type: "number"},
};